import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FullscreenDirective } from '@epicuro-next/ataraxia/fullscreen';
import { dynamicElementsListProducts } from '@epicuro-next/constants/dynamic-elements';
import { DataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import { ProductSearchListMode } from '@epicuro-next/features/lists/list-content';
import { IDynamicBindableValue } from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  APP_PRIVATE_PATHS,
  PRODUCT_LIST_SUBROUTES,
} from '@epicuro-next/navigation/constants';
import {
  createDynamicEventName,
  fromDynamicEvent,
} from '@epicuro-next/platform/dynamic-actions';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import { isNonNullish } from '@epicuro-next/utilities';
import { Product, SearchSortType } from 'graphql-api/gql-api-interfaces';
import { combineLatest, Subject } from 'rxjs';
import {
  delayWhen,
  filter,
  map,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';

import { ListProductsComponentStore } from './list-products.component-store';

@DynamicComponentRegister(dynamicElementsListProducts)
@Component({
  selector: 'epc-dynamic-elements-list-products',
  templateUrl: './list-products.component.html',
  styleUrls: ['./list-products.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ListProductsComponentStore],
  standalone: false,
})
export class ListProductsComponent implements OnInit, OnDestroy {
  @ViewChild(FullscreenDirective)
  public fullscreen: FullscreenDirective | null = null;

  public skeletons = new Array(25).fill('');
  public productListPath = APP_PRIVATE_PATHS.productList.path;
  public bulkAddProductsPath = PRODUCT_LIST_SUBROUTES.bulkAdd;
  public productListEditPath = PRODUCT_LIST_SUBROUTES.edit;
  public isFullscreen = false;
  private destroy$ = new Subject<void>();

  @Input()
  public set list(value: IDynamicBindableValue) {
    const { provider, property } = value;
    const service = this.dataProvidersResolver.resolve(provider);

    if (!service) {
      return;
    }

    service
      .read<string>(property)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.componentStore.setList(id);
        this.componentStore.loadProducts();
      });
  }

  public list$ = this.componentStore.list$;
  public groupedByCategory$ = this.componentStore.sortType$.pipe(
    map((sortType) => {
      return sortType === SearchSortType.Category;
    }),
  );
  public showUnavailableProducts$ =
    this.componentStore.showUnavailableProducts$;
  private isSalesRepOrAdmin$ = this.componentStore.isSalesRepOrAdmin$;
  public total$ = this.componentStore.total$;
  public totalUnavailable$ = this.componentStore.totalUnavailable$;
  public totalAvailable$ = this.componentStore.totalAvailable$;
  public products$ = this.componentStore.products$;
  public allProductsLoaded$ = this.componentStore.allProductsLoaded$;
  public loading$ = this.componentStore.loading$;
  public isOwner$ = this.componentStore.isOwner$;
  public isCommercialList$ = this.componentStore.isCommercialList$;
  public isEpicShow$ = this.componentStore.isEpicShow$;
  public isDraggable$ = this.componentStore.isDraggable$;
  public isListDeleted$ = this.componentStore.isListDeleted$;
  public totalProducts$ = this.componentStore.totalProducts$;
  public productReferences$ = this.componentStore.productReferences$;
  public isCommercialListWithMultipleDates$ =
    this.componentStore.isCommercialListWithMultipleDates$;
  public showProductsAutocomplete$ =
    this.componentStore.showProductsAutocomplete$;

  public isEpicShowConsumer$ = combineLatest([
    this.isOwner$,
    this.isSalesRepOrAdmin$,
    this.isEpicShow$,
  ]).pipe(
    map(([isOwner, isSalesRepOrAdmin, isEpicShow]) => {
      return isEpicShow && !isOwner && !isSalesRepOrAdmin;
    }),
  );

  public initialRatio$ = this.list$.pipe(
    map((list) => {
      if (!list || !list.mainMedia?.width || !list.mainMedia?.height) {
        return undefined;
      }

      const { width, height } = list.mainMedia;
      const ratio = (width ?? 0) / (height ?? 0);

      return !isNaN(ratio) && ratio > 0 ? ratio : undefined;
    }),
  );

  public productSearchListMode$ = this.isCommercialList$.pipe(
    map((isCommercial) =>
      isCommercial
        ? ProductSearchListMode.Commercial
        : ProductSearchListMode.Default,
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public categoryName$ = this.environmentService.environmentSettings.pipe(
    map((value) => value?.basketItemCategoryAttributeKey),
    filter(isNonNullish),
  );

  constructor(
    private componentStore: ListProductsComponentStore,
    private dataProvidersResolver: DataProvidersResolver,
    private environmentService: EnvironmentService,
  ) {}

  public ngOnInit() {
    this.subscribeFullScreenChange();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onScroll() {
    this.loading$
      .pipe(
        delayWhen(() => this.list$.pipe(filter((list) => !!list))),
        take(1),
        filter((loading) => !loading),
      )
      .subscribe(() => {
        this.componentStore.loadProducts();
      });
  }

  public productSorted({ id, newIndex }: { id: string; newIndex: number }) {
    this.componentStore.reorderProductInList(id, newIndex);
  }

  public addProductsManually() {
    this.componentStore.addProductsManually();
  }

  public clickedProductCard(product: Product, index: number) {
    this.closeFullscreenIfOpened();
    this.componentStore.cardClick(product, index);
  }

  public trackByFn(_: number, p: Product) {
    return p.productId;
  }

  public closeFullscreenIfOpened() {
    if (this.fullscreen?.opened) {
      this.fullscreen.close();
    }
  }

  private subscribeFullScreenChange() {
    fromDynamicEvent(createDynamicEventName('epc-epic-show-fullscreen-change'))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.fullscreen) {
          return;
        }

        if (this.fullscreen.opened) {
          this.fullscreen.close();

          return;
        }

        this.fullscreen.open();
      });
  }
}
